<script>
  import InterestRateResult from '@components/InterestRateResult.svelte'; // InterestRateResult コンポーネントをインポート
  import {
    SavingInterestRateNumber,
    SavingPremiumInterstRateNumber,
    AllDepositNumber
  } from "@util/constant";

  const clearIcon = "/assets_default/icons/clear.svg"
  // --- 状態変数 ---
  let amountInput = ''; // 預入金額の入力値
  let isAmountInputFocused = false; // 入力フィールドがフォーカスされているか
  let amountInputTouched = false; // 入力フィールドが一度でも触れられた（blurされた）か

  let isAmountInputError = false; // 入力フィールドがエラー状態か
  let amountInputErrorMessage = ''; // エラーメッセージ

  // 預入期間のラジオボタンの選択値、初期値は「1ヶ月」
  let selectedPeriod = '1month';

  // InterestRateResult コンポーネントに渡すための計算結果オブジェクト
  // 計算が実行されるまでnull
  let calculatedOutput = null;

  // input要素への参照 (クリア後にフォーカスを戻すために使用)
  let amountInputField;

  // 定数
  const TAX_RATE = 0.20315; // 税率 20.315%

  // 金利タイプと実際の金利のマップ (小数表記)
  const RATE_TYPES = {
    A: SavingPremiumInterstRateNumber / 100, // 0.57%
    B: SavingInterestRateNumber / 100, // 0.37%
    C: AllDepositNumber / 100, // 0.30%
  };

  // --- リアクティブなバリデーションロジック ---
  $: {
    // カンマを除去した状態でバリデーションを行う
    const value = amountInput.replace(/,/g, '').trim();
    let newIsError = false;
    let newErrorMessage = '';

    if (value === '') {
      if (amountInputTouched) {
        newIsError = true;
        newErrorMessage = '入力は必須です。';
      }
    } else {
      // カンマ除去後の値が数字のみかチェック
      if (!/^\d+$/.test(value)) {
        newIsError = true;
        newErrorMessage = '半角数字で入力してください。';
      } else {
        const numValue = Number(value);
        if (!Number.isInteger(numValue) || numValue < 0) {
          newIsError = true;
          newErrorMessage = '有効な整数を入力してください。';
        } else if (numValue >= 100000000) {
          newIsError = true;
          newErrorMessage = '預入金額は1億円未満で入力してください。';
        }
      }
    }

    isAmountInputError = newIsError;
    amountInputErrorMessage = newErrorMessage;
  }


  // --- イベントハンドラ ---
  function handleAmountInputFocus() {
    isAmountInputFocused = true;
    // フォーカスした時はカンマを取り除いて編集しやすくする
    amountInput = amountInput.replace(/,/g, '');

    if (amountInput.trim() === '' && amountInputErrorMessage === '入力は必須です。') {
      isAmountInputError = false;
      amountInputErrorMessage = '';
    }
  }

  function handleAmountInputBlur() {
    isAmountInputFocused = false;
    amountInputTouched = true;

    // フォーカスが外れた時、エラーがなければカンマ区切りにフォーマットする
    if (!isAmountInputError && amountInput.trim() !== '') {
      const num = Number(amountInput.replace(/,/g, ''));
      if (!isNaN(num)) {
        amountInput = num.toLocaleString();
      }
    }
  }

  function handleClearClick() {
    amountInput = '';
    amountInputTouched = true;
    amountInputField.focus();
  }

  // --- リアクティブな計算ボタンの活性/非活性状態 ---
  $: canCalculate = !isAmountInputError && amountInput.trim() !== '' && selectedPeriod !== '';

  // --- 入力値が変更されたら計算結果をリセット ---
  $: amountInput, selectedPeriod, (() => {
    calculatedOutput = null;
  })();


  // --- 通貨表示フォーマット ---
  function formatCurrency(value) {
    if (value === null || isNaN(value)) return '';
    // すでに数値が入ってくる想定
    return Math.floor(value).toLocaleString();
  }

   // --- 預入期間の表示ラベルを取得 ---
  function getPeriodLabel(periodKey) {
    switch (periodKey) {
      case '1month': return '1ヶ月';
      case '6month': return '6ヶ月';
      case '1year': return '1年';
      default: return '';
    }
  }

  // --- 利息計算ヘルパー関数 ---
  function calculateInterest(principal, annualRate, period) {
    let interestBeforeTax = 0;

    switch (period) {
      case '1month':
        interestBeforeTax = principal * annualRate / 12;
        break;
      case '6month':
        interestBeforeTax = principal * annualRate / 2;
        break;
      case '1year':
        interestBeforeTax = principal * annualRate;
        break;
      default:
        interestBeforeTax = 0;
    }

    return Math.floor(interestBeforeTax * (1 - TAX_RATE)); // 税引後、切り捨て
  }

  // --- 計算処理 ---
 function calculate() {
    if (!canCalculate) return;

    // カンマを除去して数値に変換
    const principal = Number(amountInput.replace(/,/g, ''));

    const resultForProp = {
      // 表示用に計算時の入力値を保存しておく
      inputSnapshot: {
        amount: principal,
        period: selectedPeriod
      },
      saving: {
        premium : {
          interest: (RATE_TYPES.A * 100),
          interestTaxMinus: calculateInterest(principal, RATE_TYPES.A, selectedPeriod),
        },
        notPremium : {
          interest: (RATE_TYPES.B * 100),
          interestTaxMinus: calculateInterest(principal, RATE_TYPES.B, selectedPeriod),
        }
      },
      deposit: {
        interest: (RATE_TYPES.C * 100),
        interestTaxMinus: calculateInterest(principal, RATE_TYPES.C, selectedPeriod),
      }
    };

    calculatedOutput = resultForProp;
  }
</script>

<div class="simulation-container">
  <p class="simulation-head owd-text:01"><strong>シミュレーション</strong></p>
  <div class="simulation-content spacing:bottom:24">
    <div class="owd-amountinput">
      <div class="owd-amountinput-wrapper" id="defaultInputComponent">
        <!-- 預入金額セクション -->
        <div class="owd-amountinput-container spacing:bottom:24" class:is-error={isAmountInputError}>
          <div class="simulation-content-head spacing:bottom:4">
            <p class="owd-text:02"><strong>預入金額</strong></p>
            <div class="owd-servicesticker owd-servicesticker:small">
              <span class="owd-information text-color:white"><strong>必須</strong></span>
            </div>
          </div>
          <div class="simulation-content-body">
            <div class="owd-amountinput-inputarea">
              <div class="owd-amountinput-inputarea:input">
                <input
                  type="text"
                  id="myInput"
                  class="input-field"
                  placeholder="預入金額を入力してください"
                  bind:value={amountInput}
                  on:focus={handleAmountInputFocus}
                  on:blur={handleAmountInputBlur}
                  bind:this={amountInputField}
                />
                {#if amountInput.trim() !== ''}
                  <span class="owd-amountinput-clearbutton" on:click={handleClearClick}>
                    <img src={clearIcon} alt="クリアアイコン" />
                  </span>
                {/if}
              </div>
              <p class="owd-heading:03">円</p>
            </div>
          </div>
          {#if isAmountInputError}
            <p class="owd-amountinput-error owd-caption">{amountInputErrorMessage}</p>
          {/if}
        </div>

        <!-- 預入期間セクション -->
        <div class="owd-amountinput-container">
          <div class="simulation-content-head spacing:bottom:24">
            <p class="owd-text:02"><strong>預入期間</strong></p>
            <div class="owd-servicesticker owd-servicesticker:small">
              <span class="owd-information text-color:white"><strong>必須</strong></span>
            </div>
          </div>
          <div class="simulation-content-body spacing:bottom:32">
            <ul class="simulation-radio-list">
              <li>
                <label>
                  <input type="radio" class="option-input radio" name="period" value="1month" bind:group={selectedPeriod} />
                  <span class="owd-text:02 option-label"><strong>1ヶ月</strong></span>
                </label>
              </li>
              <li>
                <label>
                  <input type="radio" class="option-input radio" name="period" value="6month" bind:group={selectedPeriod} />
                  <span class="owd-text:02 option-label"><strong>6ヶ月</strong></span>
                </label>
              </li>
              <li>
                <label>
                  <input type="radio" class="option-input radio" name="period" value="1year" bind:group={selectedPeriod} />
                  <span class="owd-text:02 option-label"><strong>1年</strong></span>
                </label>
              </li>
            </ul>
          </div>
        </div>

        <div class="block:center">
          <button class="owd-button owd-button:small owd-button:secondary" disabled={!canCalculate} on:click={calculate}>計算する</button>
        </div>
      </div>
    </div>
  </div>

  <ul class="owd-list:annotation spacing:bottom:40">
    <li>預入金額は、1億円未満の整数を入力してください。カンマの入力はできません。</li>
  </ul>

  {#if calculatedOutput !== null}
    <div class="simulation-content spacing:bottom:40">
      <p class="owd-text:01 align:center spacing:bottom:12"><strong>入力条件</strong></p>

      <div class="simulation-price">
        <p class="owd-text:02 spacing:bottom:8"><strong>預入金額</strong></p>
        <!-- inputSnapshotから取得することでカンマなしの数値を確実に渡す -->
        <p class="owd-text:02">{formatCurrency(calculatedOutput.inputSnapshot.amount)}円</p>
      </div>

      <div class="simulation-price">
        <p class="owd-text:02 spacing:bottom:8"><strong>預入期間</strong></p>
        <!-- inputSnapshotから取得 -->
        <p class="owd-text:02">{getPeriodLabel(calculatedOutput.inputSnapshot.period)}</p>
      </div>
    </div>

    <!-- コンポーネントへの渡し方はそのまま -->
    <InterestRateResult result={calculatedOutput} />
  {/if}

</div>

<style lang="scss">

  .simulation-content {
    background: g.$bglightgray;
    padding: 32px 24px;

    &-head {
      gap: 4px;
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }
  }

.simulation-head {
  background: var(--primary-black);
  color: var(--primary-white);
  padding: 13.5px;
  color: #fff;
  text-align: center;
}

.simulation-radio-list {
  li {
    margin-bottom: 20px;
  }
}

label{
  display:grid;
  grid-template-columns: 24px 1fr;
  gap: 4px;
  align-items: center;
  line-height:24px;
}
.option-input {
  padding: 2px;
  appearance: none;
  height: 20px;
  width: 20px;
  min-height: 20px;
  max-height: 20px;
  max-width: 20px;
  background: #cbd1d8;
  border: none;
  color: #fff;
  cursor: pointer;
  display: block;
  outline: none;
  position: relative;
  border-radius: 100%;
  z-index: 4;
  border: 2px solid #000;
  background: transparent;
}
.option-input:checked {
  background: #000;
  &:after {
    border-right: 2px solid #fff;
    border-top: 2px solid #fff;
    width: 9px;
    height: 4px;
    content: "";
    position: absolute;
    top: calc(50% - 3px);
    left: calc(50% - 11px);
    transform: translate(-50%,-50%);
    rotate: 135deg;
  }
}

.option-label {
  padding: 5px 0;

}

button {
  &:disabled {
    opacity: .1;
  }
}

.simulation-price {
  border-bottom: 1px solid var(--black-opacity3);
  padding-bottom: 24px;
  margin-bottom: 24px;
  &:last-child {
    margin-bottom: 0;
  }
}

</style>
